import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroChevronRightSolid,
  heroMagnifyingGlassSolid,
} from '@ng-icons/heroicons/solid';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ITenant } from '../interfaces/tenant.interface';
import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';

@Component({
  selector: 'app-select-organization',
  imports: [CommonModule, ReactiveFormsModule, NgIcon, RouterModule],
  providers: [
    AuthService,
    provideIcons({ heroMagnifyingGlassSolid, heroChevronRightSolid }),
  ],
  templateUrl: './select-organization.component.html',
  styleUrl: './select-organization.component.scss',
})
export class SelectOrganizationComponent implements OnInit {
  tenantService = inject(TenantService);
  authService = inject(AuthService);
  searchControl = new FormControl('');
  selectedIndex = signal(-1);

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value) => {
        this.tenantService.setSearchQuery(value || '');
        this.selectedIndex.set(-1); // Reset selection on search
      });
  }
  async navigateToTenant(tenant: ITenant): Promise<void> {
    const refresh_token = await this.authService.getSecuredToken(
      tenant.subdomain,
    );

    const externalUrl = `${tenant.login_callback_url}?refresh_token=${refresh_token}`;
    window.location.href = externalUrl;
  }

  logout(): void {
    this.authService.logout();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const tenants = this.tenantService.filteredTenants(); // Get current tenant list

    if (tenants.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex.set(
          Math.min(this.selectedIndex() + 1, tenants.length - 1),
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex.set(Math.max(this.selectedIndex() - 1, 0));
        break;

      case 'Enter':
        if (this.selectedIndex() >= 0) {
          this.navigateToTenant(tenants[this.selectedIndex()]);
        }
        break;
    }

    setTimeout(() => {
      const selectedElement = document.querySelector(
        '.tenant-item.bg-gray-200',
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }, 0);
  }

  isSelected(index: number): boolean {
    return this.selectedIndex() === index;
  }
}
